// src/components/SubmitTime/types.ts
// TypeScript types for SubmitTime component

export const FieldTypeId = {
  HyperLink: 1,
  NumericInteger: 2,
  Text: 3,
  NumericDecimal: 4,
  ComboBox: 5,
  MultilineText: 6,
  Date: 7,
  MultiSelect: 8,
  Image: 9,
  RichText: 10,
  Summernote: 11,
} as const;
export type FieldTypeId = (typeof FieldTypeId)[keyof typeof FieldTypeId];

export interface CustomField {
  CustomFieldId: number;
  CustomFieldValueId?: number;
  Name: string;
  Value: string | number | null;
  FieldTypeId: FieldTypeId;
  FieldType?: {
    FieldTypeId: FieldTypeId;
    Name: string;
  };
  SelectedValuesText?: string;
  Options?: CustomFieldOption[];
  ConversationIsEnable: boolean;
  IsRequired?: boolean;
  MaxLength?: number;
  MinValue?: number;
  MaxValue?: number;
  Placeholder?: string;
  HelpText?: string;
}

export interface CustomFieldOption {
  Id: number;
  Text: string;
  Value: string | number;
  IsSelected?: boolean;
}

export interface CustomFieldProps {
  customField: CustomField;
  canEdit: boolean;
  EditorEnabled?: boolean;
  disableAutoSave?: boolean;
  apiConfig?: ApiConfig;
  configValueId?: number;
  layerId?: number;
  projectId?: number | null;
  conversation?: ConversationItem;
  groups?: any[];
  UpdateCustomFields?: (customField: CustomField) => void;
  onCustomFieldUpdated?: (data: any) => void;
  handleDynamicInputChange: (fieldId: number, value: any, displayText?: string) => void;
  isConversationDetailsView?: boolean;
  SocketConnectionId?: string;
  onOpenEditHistory?: (field: CustomField) => void;
  updateCustomFieldsHistory?: (field: CustomField) => void;
  loadingBarStart?: () => void;
  loadingBarComplete?: () => void;
  showRequiredError?: boolean;
}

export interface ApiConfig {
  apiUrl?: string;
  authToken?: string;
  apiHeader?: Record<string, string>;
  canEdit?: boolean;
  canDelete?: boolean;
  isAdmin?: boolean;
  tenancyName?: string;
  token?: string;
  refreshToken?: string;
  TenancyName?: string;
}

export interface ConversationLayerItem {
  LayerId: number;
  LayerName: string;
  LayerColor?: string;
  IsDefault?: boolean;
  IsActive?: boolean;
}

export interface ConversationItem {
  ConversationId: number;
  ConfigValueId: number;
  selectedConfigValueId?: number;
  ProjectID?: number;
  ProjectId?: number;
  CoreTaskId?: number;
  CoreTaskID?: number;
  OwnedById?: number;
  CreatedById?: number;
  IsEnable?: boolean;
  HasPhases?: boolean;
  Name?: string;
  Title?: string;
  Body?: string;
  Attachments?: Attachment[];
  CustomFields?: CustomField[];
  ConversationLayers?: ConversationLayerItem[];
}

export interface Attachment {
  AttachmentId: number;
  /** Server-side GUID (type Guid in C# AttachmentVM). Comes from the upload endpoint.
   *  For the loading placeholder it temporarily holds the human-readable name but
   *  placeholders are filtered before the payload is built. */
  FileName: string;
  FileUrl: string;
  FileSize: number;
  FileType: string; // MIME type — kept for local/display use
  FileContentType?: string; // MIME type expected by C# AttachmentVM
  /** Extension WITH leading dot — e.g. ".pdf". C# uses Description+FileExtension
   *  to reconstruct the original filename for downloads. */
  FileExtension?: string;
  /** Original human-readable filename WITHOUT extension (matches C# AttachmentVM.Description).
   *  This is what AttachmentsContent displays next to the file icon. */
  Description?: string;
  ThumbnailUrl?: string;
  TempFile?: string; // server temp path after upload; included in reply payload
  isUploading?: boolean;
}

export interface Phase {
  PhaseId: number;
  PhaseName: string;
  ProjectId: number;
  Order: number;
  IsActive: boolean;
}

export interface Project {
  ProjectId: number;
  ProjectName: string;
  ConfigValueId: number;
}

export interface TimeEntry {
  TimeTrackingId: number;
  ConversationId: number;
  Hours: number;
  Date: string;
  Description: string;
  Estimated?: number;
  Remaining?: number;
  Actual?: number;
  PhaseId?: number;
  ProjectId?: number;
  TypeId?: number;
  TaskId?: number;
  TeamId?: number;
  CustomFields?: CustomField[];
  Attachments?: Attachment[];
}

export interface SubmitTimeProps {
  api: any; // API instance
  title?: string;
  isdraggable?: boolean;
  closeonescape?: boolean;
  allowMinimize?: boolean;
  allowMaximize?: boolean;
  hasmaxheight?: boolean;
  typesubmittime: 1 | 2; // 1 = from conversation, 2 = standalone
  conversation?: ConversationItem;
  openmodal: boolean;
  width?: number;
  height?: number;
  phaseselected?: Phase | null;
  isedit?: boolean;
  objedit?: TimeEntryEdit | null;
  socketconnectionid?: string;
  corgeefunctions?: any;
  apiConfig?: ApiConfig;
  onClose?: () => void;
  onclose?: () => void;
  onSave?: () => void;
  onSubmitTime?: (conversation?: any) => void;
  onaddreply?: (
    conversationId: number,
    reply: any,
    isFromSubmitTime?: boolean,
    flag?: boolean
  ) => void;
  onaddreplywarning?: (
    conversationId: number,
    reply: any,
    isFromSubmitTime?: boolean,
    flag?: boolean
  ) => void;
  onupdatereply?: (reply: any, replyId?: number) => void;
  onSubmitSuccess?: (data: any) => void;
  onSubmitError?: (error: any) => void;
}

export interface ValidationState {
  estimated: boolean;
  date: boolean;
  hours: boolean;
  hoursOutOfRange: boolean;
  remaining: boolean;
  description: boolean;
  selectedId: boolean;
  remeaning: boolean;
  maxHours: boolean;
  disabledLayers: boolean;
  showPhases: boolean;
  validPhases: boolean;
  disabledPhases: boolean;
  showProjects: boolean;
  validProject: boolean;
  disabledProject: boolean;
  showType: boolean;
  validType: boolean;
  disabledType: boolean;
  showTask: boolean;
  validTask: boolean;
  disabledTask: boolean;
}

export interface SubmitTimeState {
  openmodal: boolean;
  timeDialog: ConversationItem | null;
  configValueId: number;
  projectId: number | null;
  selectedId: LayerItem | null;
  dataLayers: LayerItem[];
  estimatedField: string;
  remainingField: string;
  remainingOriginal: string;
  hours: string;
  description: string;
  remainingInitial: string;
  date: string;
  maxMinutesPerDay: number;
  timeTracking: TimeEvent[];
  customFieldId: string;
  btnSubmit: boolean;
  valForm: ValidationState;
  actualField: string;
  actualFieldInitial: string;
  customFieldActualId?: number;
  dataPhases: PhaseItem[];
  phaseSelected: PhaseItem | null;
  projectSelected: ProjectItem | null;
  dataProjects: ProjectItem[];
  typeSelected: TypeItem | null;
  dataType: TypeItem[];
  taskSelected: TaskItem | null;
  dataTasks: TaskItem[];
  body: string;
  attachments: Attachment[];
  isNewRepost: boolean;
  isedit: boolean;
  objedit: TimeEntryEdit | null;
  isOpenQuestionDelete: boolean;
  hoursInitial: string;
  remeaningFormat: boolean;
  hoursFormat: boolean;
  customKey: number;
  warningReplyId: number;
  timeTrackingTeam: boolean;
  defaultTeams: Team[];
  selectedTeam: Team | null;
  hoursAdjust: number;
  minutesAdjust: number;
  addedConfigurations: CustomFieldConfig[] | null;
  warningRemainingDays: boolean;
  remindRemaining: boolean;
  backupCustomFields: CustomFieldConfig[];
}

export interface SelectOption {
  value: string | number;
  label: string;
}

export interface Team {
  TeamId: number;
  Team?: string; // API field name from GetDefaultTeams
  TeamName?: string;
  Name?: string;
  IsDefault?: boolean;
}

export interface TeamResponse {
  defaultteam: Team | null;
  teams: Team[];
  submittimeteam: boolean;
}

// Additional types for SubmitTime legacy migration

export interface LayerItem {
  LayerId: number;
  Name: string;
  Color?: string;
  CoreTaskId?: number;
  CoreTaskID?: number;
}

export interface PhaseItem extends Phase {
  Name?: string; // Alias for PhaseName
  Title?: string;
  CoreTaskId?: number;
  CoreTaskID?: number;
  ConversationId?: number;
  ConversationPhaseId?: number;
}

/** "All Phases" (ConversationPhaseId 0) or a phase without CoreTaskId cannot receive time */
export const isRealPhase = (phase: PhaseItem | null | undefined): phase is PhaseItem =>
  !!phase && (phase.ConversationPhaseId ?? 0) > 0 && (phase.CoreTaskId || phase.CoreTaskID || 0) > 0;

export interface ProjectItem extends Project {
  Name?: string; // Alias for ProjectName
}

export interface TypeItem {
  ConfigValueID: number | null;
  Text: string;
}

export interface TaskItem {
  ConversationId: number;
  CoreTaskID: number;
  Name: string;
  Title?: string; // Alias for Name
  HasPhases?: boolean;
}

export interface TimeEntryEdit {
  TimeTrackingID?: number;
  LayerId: number;
  ProjectID: number;
  ProjectName: string;
  ConfigValueID: number;
  SideMenuText: string;
  ConversationId: number;
  CoreTaskID: number;
  title: string;
  Hours: string;
  date: string;
  Description: string;
  attachments: Attachment[];
  TeamId?: number;
  replyId?: number;
}

export interface ReplyItem {
  ConversationId: number;
  Body: string;
  Attachments?: Attachment[];
  TagPeople?: number[];
  ReplyId?: number;
  SocketConnectionId?: string;
}

export interface ReplyLayer {
  LayerId: number;
  LayerColor?: string;
  LayerName: string;
  ReplyId: number;
}

export interface TagPerson {
  PersonId: number;
  ConversationId: number;
  ReplyId: number;
  ReplyFollowing: number;
}

export interface ReplyRequest {
  ReplyId: number;
  ConversationId: number;
  ReplyLayers: ReplyLayer[];
  Attachments: Attachment[];
  Body: string;
  Tags: TagPerson[];
  Socketconnectionid: string;
  type: number;
  ConversationPhaseId: number;
  possibleTags: number[];
  IsEnable?: boolean;
  PhaseName?: string;
  /** ReplyId of the submit time reply this overtime warning belongs to */
  SubmitReplyWarningId?: number;
}

/** Time tracking item matching the legacy API field names */
export interface TimeTrackingItem {
  TimeTrackingID?: number;
  TimeTrackingId?: number;
  ConversationId?: number;
  ConfigValueID?: number;
  CoreTaskID?: number;
  LayerId: number;
  ProjectID?: number;
  CustomFieldId?: number;
  CustomFieldValue?: string;
  CustomFieldActualId?: number;
  CustomFieldActualValue?: string;
  CustomFields?: any[];
  Discriminator?: string;
  Date: string;
  Hours: string;
  Estimated?: string;
  Remaining?: string;
  Actual?: string;
  Description?: string;
  Socketconnectionid?: string;
  warningEstimated?: string;
  TeamId?: number | null;
  ReplyId?: number;
}

export interface CustomFieldValue {
  CustomFieldId: number;
  Value: string | number | null;
  CustomFieldValueId?: number;
}

export interface CustomFieldConfig extends CustomField {
  Editable: boolean;
  Required: boolean;
}

export interface CustomFieldResponse {
  LayerId: number;
  Color?: string;
  IsDefault?: boolean;
  IsEnable?: boolean;
  Name?: string;
  TextColor?: boolean;
  PriorityInConfigValue?: number;
  CustomFields: CustomFieldConfig[];
}

export interface SubmitTimeConfiguration {
  Id?: number;
  ConfigValueID?: number;
  ItemType?: string;
  LayerId?: number;
  LayerName?: string;
  CustomFieldId: number;
  CustomField?: string;
  Required: boolean;
  Editable: boolean;
}

export interface TimeEvent {
  ConversationId: number;
  Hours: string;
  Date: string;
  Minutes: number;
}

// API Request/Response types

export interface GetCustomFieldsRequest {
  CoreTaskId: number;
  IsFromConversation: boolean;
  ProjectId?: number;
}

export interface GetTasksRequest {
  ConfigValueID: number;
  ProjectID: number;
}

export interface GetEventsRequest {
  StartDate: string;
  EndDate: string;
}

export interface TimeTrackingRequest {
  ConversationId?: number;
  LayerId: number;
  Date: string;
  Hours: string;
  ProjectId?: number;
  ConfigValueId?: number;
  CoreTaskId?: number;
  Estimated?: string;
  Remaining?: string;
  Actual?: string;
  CustomFields?: CustomFieldValue[];
  TeamId?: number;
  ReplyId?: number;
}

export interface DeleteTimeEntryRequest {
  TimeTrackingId: number;
}

export interface DeleteAndUpdateRequest {
  TimeTrackingID: number;
  CoreTaskID?: number;
  ProjectID?: number;
  Date: string;
  Socketconnectionid?: string;
  Description?: string;
  CustomFieldId?: number;
  CustomFieldValue?: string | number;
  CustomFieldActualId?: number;
  CustomFieldActualValue?: string | number;
  Discriminator?: string;
}

export interface DeleteReplyRequest {
  ReplyId: number;
  ConversationId: number;
  Body: string;
  SocketConnectionId?: string;
  ConversationPhaseId?: number;
}

export interface UpdateCustomFieldRequest {
  CustomFieldValueId: number;
  Value: string | number;
  CustomFieldId: number;
}
